/**
 * useWithdrawActions Hook
 * 본사 입출금 신청 및 출금 가능 금액 조회 전용 훅
 */
import { useCallback, useState } from 'react';
import { headWithdrawApi } from '../api/headWithdrawApi';
import {
    HeadGetWithdrawableAmountResponseDto,
    HeadRequestWithdrawRequestDto,
    HeadRequestDepositRequestDto
} from '../types/headWithdraw.types';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';

export const useWithdrawActions = () => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [withdrawableAmount, setWithdrawableAmount] = useState<HeadGetWithdrawableAmountResponseDto | null>(null);

    const fetchWithdrawableAmount = useCallback(async () => {
        try {
            setLoading(true);
            const response = await headWithdrawApi.getWithdrawableAmount();
            setWithdrawableAmount(response || null);
        } catch (error: unknown) {
            if (error && typeof error === 'object' && 'response' in error) {
                const axiosError = error as { response?: { status?: number } };
                if (axiosError.response?.status !== 404) {
                    showToast('출금가능금액을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
                }
            } else {
                showToast('출금가능금액을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            }
            setWithdrawableAmount(null);
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    const requestWithdraw = useCallback(async (
        suppId: string,
        reqVal: number,
        briefs?: string
    ) => {
        try {
            setLoading(true);
            const request: HeadRequestWithdrawRequestDto = { suppId, reqVal };
            if (briefs) {
                request.briefs = briefs;
            }
            const response = await headWithdrawApi.requestWithdraw(request);

            if (response.success) {
                showToast('출금 신청이 완료되었습니다.', { type: ToastType.SUCCESS });
                await fetchWithdrawableAmount();
                return response;
            } else {
                showToast(response.message || '출금 신청에 실패했습니다.', { type: ToastType.ERROR });
                return null;
            }
        } catch {
            showToast('출금 신청에 실패했습니다.', { type: ToastType.ERROR });
            return null;
        } finally {
            setLoading(false);
        }
    }, [showToast, fetchWithdrawableAmount]);

    const requestDeposit = useCallback(async (
        userType: string,
        reqVal: number,
        briefs?: string
    ) => {
        try {
            setLoading(true);
            const request: HeadRequestDepositRequestDto = { userType, reqVal };
            if (briefs) {
                request.briefs = briefs;
            }
            const response = await headWithdrawApi.requestDeposit(request);

            if (response.success) {
                showToast('입금 신청이 완료되었습니다.', { type: ToastType.SUCCESS });
                return response;
            } else {
                showToast(response.message || '입금 신청에 실패했습니다.', { type: ToastType.ERROR });
                return null;
            }
        } catch {
            showToast('입금 신청에 실패했습니다.', { type: ToastType.ERROR });
            return null;
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    return {
        loading,
        withdrawableAmount,
        fetchWithdrawableAmount,
        requestWithdraw,
        requestDeposit,
    };
};
